import React, { useMemo } from 'react';
import {
    Box, Tooltip,
} from '@mui/material';
import {
    GlColumnDef, GlSheetRowVO, buildColumnKey, isBalanceSheetFsCode,
    isLyPlaceholderGlCode, resolvePriorYearAmount,
} from '@/services/accountService/glInvoiceService';
import { resolveGlCurrentPeriodDisplay } from '@/utils/glToFsMapper';
import {
    fxRateColumnKey,
    hkdCounterpartColumnKey,
    isBankCashColumn,
    isFxOriginalColumn,
    resolveFxCurrency,
    resolveFxTrios,
    resolveRateValue,
    type FxBankTrio,
} from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { columnHeaderLabelWithAlias } from '@/pages/components/account/gl-invoice/columnAliasDisplay';

interface GlSheetTableProps {
    columnDefs: GlColumnDef[];
    rows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    /** 表头是否显示 Co.GL 客户别名 */
    showClientAlias?: boolean;
}

/**
 * Original Mode 列：与 Excel 一致，银行外币保留 原币 / Ex-rate / Ex HKD 分列（不合并成 HKD S/A）。
 */
type DisplayCol =
    | { type: 'amount'; def: GlColumnDef; colKey: string; colIndex: number }
    | { type: 'fx-rate'; label: string; trio: FxBankTrio; colIndex: number };

/** 構建顯示列：每條 columnDef 一列；外币組在原幣後插入 Ex-rate（API 常不帶 RATE 列） */
function buildDisplayColumns(defs: GlColumnDef[]): DisplayCol[] {
    const sorted = [...defs].sort((a, b) => a.columnIndex - b.columnIndex);
    const trios = resolveFxTrios(defs);
    const trioByOriginal = new Map(trios.map((t) => [t.originalKey, t]));
    // 若 defs 已含 RATE 列，避免再插入合成列
    const existingKeys = new Set(sorted.map((d) => buildColumnKey(d)));

    const cols: DisplayCol[] = [];
    for (const d of sorted) {
        if (isLyPlaceholderGlCode(d.glCode)) continue;
        const colKey = buildColumnKey(d);
        cols.push({ type: 'amount', def: d, colKey, colIndex: d.columnIndex });

        const trio = trioByOriginal.get(colKey);
        if (trio && !existingKeys.has(trio.rateKey)) {
            cols.push({
                type: 'fx-rate',
                label: 'Ex-rate',
                trio,
                colIndex: d.columnIndex + 0.5,
            });
            continue;
        }
        // 只有原币列、没有 Ex HKD 配对时，仍按 *-RATE 插一列
        if (!trio && isFxOriginalColumn(d)) {
            const rateKey = fxRateColumnKey(d);
            if (!existingKeys.has(rateKey)) {
                cols.push({
                    type: 'fx-rate',
                    label: 'Ex-rate',
                    trio: {
                        originalKey: colKey,
                        rateKey,
                        hkdKey: '',
                        currency: resolveFxCurrency(d),
                    },
                    colIndex: d.columnIndex + 0.5,
                });
            }
        }
    }
    return orderAsOriginalRateHkd(cols, sorted);
}

/** 同一外币组按 原币 → 汇率 → 港币 排，港币列插到汇率后面。 */
function orderAsOriginalRateHkd(cols: DisplayCol[], defs: GlColumnDef[]): DisplayCol[] {
    const hkdKeyByOriginal = new Map<string, string>();
    for (const def of defs) {
        if (!isFxOriginalColumn(def)) continue;
        const hkdKey = hkdCounterpartColumnKey(def, defs);
        if (hkdKey) hkdKeyByOriginal.set(buildColumnKey(def), hkdKey);
    }
    for (const col of cols) {
        if (col.type !== 'fx-rate' || !col.trio.hkdKey) continue;
        hkdKeyByOriginal.set(col.trio.originalKey, col.trio.hkdKey);
    }

    const result = [...cols];
    for (const [originalKey, hkdKey] of hkdKeyByOriginal) {
        const idxOrig = result.findIndex((c) => c.type === 'amount' && c.colKey === originalKey);
        const idxRate = result.findIndex((c) => c.type === 'fx-rate' && c.trio.originalKey === originalKey);
        const idxHkd = result.findIndex((c) => c.type === 'amount' && c.colKey === hkdKey);
        if (idxOrig < 0 || idxRate < 0 || idxHkd < 0) continue;
        const group = [result[idxOrig], result[idxRate], result[idxHkd]];
        const insertAt = Math.min(idxOrig, idxRate, idxHkd);
        for (const idx of [idxOrig, idxRate, idxHkd].sort((a, b) => b - a)) {
            result.splice(idx, 1);
        }
        result.splice(insertAt, 0, ...group);
    }
    return result;
}

/** 行/匯總取值：一律精確 columnKey（銀行多列同 fsCode 不能用 sumByFsCode） */
function getAmountColValue(
    colKey: string,
    values: Record<string, number> | undefined,
): number | undefined {
    if (!values) return undefined;
    const v = values[colKey];
    return v != null && v !== 0 ? v : undefined;
}

/** 上年行：精确 key，否则回退 fsCode|LY */
function getPriorYearColValue(
    colKey: string,
    values: Record<string, number> | undefined,
): number | undefined {
    const n = resolvePriorYearAmount(colKey, values);
    return n !== 0 ? n : undefined;
}

function getFxRateColValue(
    trio: FxBankTrio,
    values: Record<string, number> | undefined,
): number | undefined {
    if (!values) return undefined;
    const original = Number(values[trio.originalKey]) || 0;
    const rate = resolveRateValue(values, trio, original);
    return rate !== 0 ? rate : undefined;
}

function getDisplayColValue(
    dc: DisplayCol,
    values: Record<string, number> | undefined,
): number | undefined {
    if (dc.type === 'fx-rate') return getFxRateColValue(dc.trio, values);
    return getAmountColValue(dc.colKey, values);
}

/** 金额列：当期/上年汇总或任一行有非零值才展示；外币组任一列有值则整组保留 */
function displayColHasData(
    dc: DisplayCol,
    rows: GlSheetRowVO[],
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
): boolean {
    const keys: string[] = [];
    if (dc.type === 'amount') {
        keys.push(dc.colKey);
    } else {
        keys.push(dc.trio.originalKey, dc.trio.rateKey);
        if (dc.trio.hkdKey) keys.push(dc.trio.hkdKey);
    }

    const nonZero = (values?: Record<string, number>) => {
        if (!values) return false;
        return keys.some((k) => {
            if (!k) return false;
            const v = Number(values[k]);
            return !Number.isNaN(v) && v !== 0;
        });
    };

    if (nonZero(summaryCurrentPeriod) || nonZero(summaryPriorYear)) return true;
    for (const row of rows) {
        if (nonZero(row.columnValues)) return true;
    }
    return false;
}

/**
 * Current Period：
 * - 銀行 / 精確列：該 key 当期 + 上年
 * - 其餘單 fsCode 非銀行：沿用 resolveGlCurrentPeriodDisplay（含 AL 等規則）
 * - Ex-rate：Excel R6 通常為空，不展示匯總
 */
function getCurrentPeriodDisplayValue(
    dc: DisplayCol,
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
    multiColFsCodes: Set<string>,
    columnDefs: GlColumnDef[],
): number | undefined {
    if (dc.type === 'fx-rate') return undefined;

    const fsCode = dc.def.fsCode;
    const isBank = isBankCashColumn(dc.def);
    const isMulti = multiColFsCodes.has(fsCode);

    if (!isBank && !isMulti) {
        const total = resolveGlCurrentPeriodDisplay(
            fsCode, summaryCurrentPeriod, summaryPriorYear, columnDefs,
        );
        return total === 0 ? undefined : total;
    }

    const movement = getAmountColValue(dc.colKey, summaryCurrentPeriod) ?? 0;
    const prior = getPriorYearColValue(dc.colKey, summaryPriorYear) ?? 0;
    const total = isBalanceSheetFsCode(fsCode) ? movement + prior : movement;
    return total === 0 ? undefined : total;
}

const STICKY_COLS = [
    { key: 'rowNum', label: '#', width: 36 },
    { key: 'date', label: 'Date', width: 88 },
    { key: 'description', label: 'Description', width: 140 },
] as const;

const SCROLL_COLS = [
    { key: 'voucherNo', label: 'Voucher No.', width: 88 },
    { key: 'invoiceNo', label: 'Invoice.', width: 88 },
    { key: 'invoiceDate', label: 'Invoice Date', width: 92 },
    { key: 'chequeNo', label: 'Cheque No.', width: 88 },
    { key: 'details', label: 'Details', width: 120 },
] as const;

const ALL_FIXED_COLS = [...STICKY_COLS, ...SCROLL_COLS];

const FIXED_TOTAL_WIDTH = ALL_FIXED_COLS.reduce((s, c) => s + c.width, 0);

function formatNum(v: number | null | undefined) {
    if (v == null || v === 0) return '';
    if (v < 0) {
        return `(${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 汇率列：多保留几位小数，与 Excel 观感接近 */
function formatRate(v: number | null | undefined) {
    if (v == null || v === 0) return '';
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

type FixedCol = typeof ALL_FIXED_COLS[number];

function getFixedColContent(col: FixedCol, row: GlSheetRowVO, rowIdx: number): React.ReactNode {
    switch (col.key) {
        case 'rowNum': return rowIdx + 1;
        case 'date': return row.date || '';
        case 'description': return row.description || '';
        case 'voucherNo': return row.voucherNo || '';
        case 'invoiceNo': return row.invoiceNo || '';
        case 'invoiceDate': return row.invoiceDate || '';
        case 'chequeNo': return row.chequeNo || '';
        case 'details': return row.details || '';
        default: return '';
    }
}

function renderFixedCell(
    col: FixedCol,
    content: React.ReactNode,
    style: React.CSSProperties,
    stickyLeft?: number,
) {
    const shouldTruncate = col.key !== 'rowNum';
    const cellStyle: React.CSSProperties = {
        ...style,
        width: col.width,
        minWidth: col.width,
        maxWidth: col.width,
        ...(shouldTruncate ? { overflow: 'hidden' } : {}),
        ...(stickyLeft != null ? { position: 'sticky', left: stickyLeft, zIndex: style.zIndex ?? 1 } : {}),
    };

    if (!shouldTruncate || content == null || content === '') {
        return (
            <td key={col.key} style={cellStyle}>
                {content}
            </td>
        );
    }

    const text = String(content);
    const truncatedInner = (
        <Box
            component="span"
            sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {content}
        </Box>
    );

    return (
        <td key={col.key} style={cellStyle}>
            <Tooltip title={text} arrow placement="top-start">
                {truncatedInner}
            </Tooltip>
        </td>
    );
}

export default function GlSheetTable(props: GlSheetTableProps) {
    const { columnDefs, rows, summaryCurrentPeriod, summaryPriorYear, showClientAlias = false } = props;

    const displayCols = useMemo(() => {
        const all = buildDisplayColumns(columnDefs);
        // 没数据的金额列不展示（空公司 / 全零科目）
        return all.filter((dc) => displayColHasData(dc, rows, summaryCurrentPeriod, summaryPriorYear));
    }, [columnDefs, rows, summaryCurrentPeriod, summaryPriorYear]);

    // 非銀行同 fsCode 多列 → Current Period 走精確 key
    const multiColFsCodes = useMemo(() => {
        const count: Record<string, number> = {};
        for (const d of columnDefs) {
            if (isBankCashColumn(d)) continue;
            count[d.fsCode] = (count[d.fsCode] || 0) + 1;
        }
        const result = new Set<string>();
        for (const [code, n] of Object.entries(count)) {
            if (n > 1) result.add(code);
        }
        return result;
    }, [columnDefs]);

    const cellSx: React.CSSProperties = {
        padding: '4px 8px',
        fontSize: 12,
        whiteSpace: 'nowrap',
        borderRight: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0',
    };

    const headerCellSx: React.CSSProperties = {
        ...cellSx,
        fontWeight: 600,
        background: '#f5f5f5',
        position: 'sticky' as const,
        top: 0,
        zIndex: 2,
    };

    return (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
                <table style={{ borderCollapse: 'collapse', minWidth: FIXED_TOTAL_WIDTH + displayCols.length * 100 }}>
                    <thead>
                        <tr>
                            {STICKY_COLS.map((col, i) => {
                                const left = STICKY_COLS.slice(0, i).reduce((s, c) => s + c.width, 0);
                                return (
                                    <th key={col.key} style={{
                                        ...headerCellSx,
                                        width: col.width,
                                        minWidth: col.width,
                                        maxWidth: col.width,
                                        position: 'sticky',
                                        left,
                                        zIndex: 4,
                                        background: '#f5f5f5',
                                    }}>
                                        {col.label}
                                    </th>
                                );
                            })}
                            {SCROLL_COLS.map((col) => (
                                <th key={col.key} style={{
                                    ...headerCellSx,
                                    width: col.width,
                                    minWidth: col.width,
                                    maxWidth: col.width,
                                }}>
                                    {col.label}
                                </th>
                            ))}
                            {displayCols.map((dc, i) => {
                                const label = dc.type === 'fx-rate'
                                    ? dc.label
                                    : columnHeaderLabelWithAlias(dc.def, showClientAlias);
                                const tip = dc.type === 'fx-rate'
                                    ? `${dc.trio.originalKey} → ${dc.trio.hkdKey}`
                                    : `${dc.def.fsCode}|${dc.def.glCode || ''}${showClientAlias && dc.def.aliasEn ? ` · ${dc.def.aliasEn}` : ''}`;
                                return (
                                    <Tooltip key={i} title={tip} arrow>
                                        <th style={{
                                            ...headerCellSx,
                                            minWidth: dc.type === 'fx-rate' ? 72 : 100,
                                            background: dc.type === 'fx-rate' ? '#FFF8E1' : '#f5f5f5',
                                        }}>
                                            {label}
                                        </th>
                                    </Tooltip>
                                );
                            })}
                        </tr>
                        <tr>
                            {STICKY_COLS.map((col, i) => {
                                const left = STICKY_COLS.slice(0, i).reduce((s, c) => s + c.width, 0);
                                return (
                                    <td key={col.key} style={{
                                        ...cellSx,
                                        width: col.width,
                                        minWidth: col.width,
                                        maxWidth: col.width,
                                        position: 'sticky',
                                        left,
                                        zIndex: 3,
                                        background: '#E3F2FD',
                                        fontWeight: 600,
                                    }}>
                                        {i === 1 ? 'Current Period Total' : ''}
                                    </td>
                                );
                            })}
                            {SCROLL_COLS.map((col) => (
                                <td key={col.key} style={{
                                    ...cellSx,
                                    width: col.width,
                                    minWidth: col.width,
                                    maxWidth: col.width,
                                    background: '#E3F2FD',
                                }} />
                            ))}
                            {displayCols.map((dc, i) => (
                                <td key={i} style={{
                                    ...cellSx,
                                    background: dc.type === 'fx-rate' ? '#FFF8E1' : '#E3F2FD',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                }}>
                                    {dc.type === 'fx-rate'
                                        ? ''
                                        : formatNum(getCurrentPeriodDisplayValue(
                                            dc, summaryCurrentPeriod, summaryPriorYear, multiColFsCodes, columnDefs,
                                        ))}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            {STICKY_COLS.map((col, i) => {
                                const left = STICKY_COLS.slice(0, i).reduce((s, c) => s + c.width, 0);
                                return (
                                    <td key={col.key} style={{
                                        ...cellSx,
                                        width: col.width,
                                        minWidth: col.width,
                                        maxWidth: col.width,
                                        position: 'sticky',
                                        left,
                                        zIndex: 3,
                                        background: '#FFF3E0',
                                        fontWeight: 600,
                                    }}>
                                        {i === 1 ? 'Last Year Total' : ''}
                                    </td>
                                );
                            })}
                            {SCROLL_COLS.map((col) => (
                                <td key={col.key} style={{
                                    ...cellSx,
                                    width: col.width,
                                    minWidth: col.width,
                                    maxWidth: col.width,
                                    background: '#FFF3E0',
                                }} />
                            ))}
                            {displayCols.map((dc, i) => (
                                <td key={i} style={{
                                    ...cellSx,
                                    background: dc.type === 'fx-rate' ? '#FFF8E1' : '#FFF3E0',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                }}>
                                    {dc.type === 'fx-rate'
                                        ? ''
                                        : formatNum(getPriorYearColValue(dc.colKey, summaryPriorYear))}
                                </td>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={ALL_FIXED_COLS.length + displayCols.length}
                                    style={{ ...cellSx, textAlign: 'center', padding: 20, color: '#999' }}>
                                    No data
                                </td>
                            </tr>
                        )}
                        {rows.map((row, rowIdx) => (
                            <tr key={row.invoiceId || rowIdx} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                {STICKY_COLS.map((col, i) => {
                                    const left = STICKY_COLS.slice(0, i).reduce((s, c) => s + c.width, 0);
                                    const content = getFixedColContent(col, row, rowIdx);
                                    return renderFixedCell(col, content, {
                                        ...cellSx,
                                        zIndex: 1,
                                        background: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                                    }, left);
                                })}
                                {SCROLL_COLS.map((col) => {
                                    const content = getFixedColContent(col, row, rowIdx);
                                    return renderFixedCell(col, content, {
                                        ...cellSx,
                                        background: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                                    });
                                })}
                                {displayCols.map((dc, i) => {
                                    const raw = getDisplayColValue(dc, row.columnValues);
                                    const bg = dc.type === 'fx-rate'
                                        ? (rowIdx % 2 === 0 ? '#FFFDE7' : '#FFF8E1')
                                        : (rowIdx % 2 === 0 ? '#fff' : '#fafafa');
                                    return (
                                        <td key={i} style={{ ...cellSx, textAlign: 'right', background: bg }}>
                                            {dc.type === 'fx-rate' ? formatRate(raw) : formatNum(raw)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
}
